import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Search } from 'lucide-react'
import { apiFetch, qs } from '../lib/api'
import { useDebounced } from '../lib/hooks'
import type { Paged, SubscriptionRow } from '../lib/types'
import { DataTable } from '../components/data-table'
import { Pagination } from '../components/pagination'
import { PageHeader, formatDate } from '../components/page'
import { ErrorState, Spinner } from '../components/states'
import { Badge, StatusBadge } from '../components/badge'

const STATUSES = ['', 'pending', 'active', 'cancelling', 'cancelled', 'failed']

const columns: ColumnDef<SubscriptionRow, any>[] = [
	{
		header: 'Customer',
		cell: ({ row }) =>
			row.original.user ? (
				<div className="min-w-0">
					<div className="truncate font-medium">{row.original.user.email}</div>
					<div className="truncate text-xs text-ink-soft">{row.original.user.name}</div>
				</div>
			) : (
				// Anonymous checkout provisions before an account exists; the row is
				// adopted when someone signs up with that email.
				<Badge tone="warning">unclaimed</Badge>
			),
	},
	{
		header: 'Plan',
		cell: ({ row }) => (
			<span className="whitespace-nowrap">
				{row.original.planSlug}
				<span className="text-ink-faint"> · {row.original.billingTerm ?? '—'}</span>
			</span>
		),
	},
	{ header: 'Status', accessorKey: 'status', cell: ({ getValue }) => <StatusBadge value={getValue<string>()} /> },
	{
		header: 'Instance',
		accessorKey: 'hostingdeId',
		cell: ({ getValue }) => <span className="mono">{getValue<string>() ?? '—'}</span>,
	},
	{
		header: 'Source',
		accessorKey: 'source',
		cell: ({ getValue }) => <Badge tone={getValue() === 'whmcs' ? 'warning' : 'neutral'}>{getValue<string>()}</Badge>,
	},
	{ header: 'Created', accessorKey: 'createdAt', cell: ({ getValue }) => formatDate(getValue<string>()) },
]

export function SubscriptionsPage() {
	const navigate = useNavigate()
	const [search, setSearch] = useState('')
	const [status, setStatus] = useState('')
	const [page, setPage] = useState(1)
	const q = useDebounced(search)

	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['subscriptions', q, status, page],
		queryFn: () => apiFetch<Paged<SubscriptionRow>>(`/api/admin/subscriptions${qs({ q, status, page })}`),
	})

	return (
		<>
			<PageHeader
				title="Subscriptions"
				subtitle="Provisioned instances and their billing. Live Chargebee data loads when you open one."
			/>

			<div className="card">
				<div className="flex flex-wrap gap-2 border-b border-line p-3">
					<div className="relative min-w-0 flex-1 sm:max-w-sm">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
						<input
							className="input pl-9"
							placeholder="Search email, company, plan, Chargebee or hosting.de id"
							value={search}
							onChange={(e) => {
								setSearch(e.target.value)
								setPage(1)
							}}
						/>
					</div>
					<select
						className="input w-auto"
						value={status}
						onChange={(e) => {
							setStatus(e.target.value)
							setPage(1)
						}}
					>
						{STATUSES.map((s) => (
							<option key={s} value={s}>
								{s === '' ? 'All statuses' : s}
							</option>
						))}
					</select>
				</div>

				{isPending ? (
					<Spinner />
				) : error ? (
					<ErrorState error={error} onRetry={refetch} />
				) : (
					<>
						<DataTable
							columns={columns}
							rows={data.rows}
							emptyMessage="No subscriptions match those filters."
							onRowClick={(row) => navigate(`/subscriptions/${row.id}`)}
						/>
						<Pagination page={data.page} limit={data.limit} total={data.total} onPageChange={setPage} />
					</>
				)}
			</div>
		</>
	)
}
